/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, cartLine));
 * document.addEventListener(ThemeEvents.quantitySelectorUpdate, (e) => { console.log(e.detail.quantity) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a shopper begins the YUSUFZ Reading quiz */
  static readingStarted = 'reading:started';
  /** @static @constant {string} Event triggered when a shopper answers a Reading question */
  static readingQuestionAnswered = 'reading:question-answered';
  /** @static @constant {string} Event triggered when a shopper finishes every Reading question */
  static readingCompleted = 'reading:completed';
  /** @static @constant {string} Event triggered once a Reading result archetype has been produced */
  static readingArchetypeProduced = 'reading:archetype-produced';
  /** @static @constant {string} Event triggered when a shopper clicks a product recommended by their Reading result */
  static readingProductClicked = 'reading:product-clicked';
  /**
   * @static @constant {string} Event triggered when a shopper adds to cart a product recommended by
   * their Reading result. Not yet dispatched anywhere -- the current result panel only links to the
   * PDP, it has no add-to-cart control of its own. Defined now so a future quick-add on that panel
   * has a name to dispatch under without another events.js change.
   */
  static readingAddToCart = 'reading:add-to-cart';
  /** @static @constant {string} Event triggered when a product is added to or removed from the wishlist */
  static wishlistUpdate = 'wishlist:update';
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for starting the YUSUFZ Reading quiz
 * @extends {Event}
 */
export class ReadingStartedEvent extends Event {
  constructor() {
    super(ThemeEvents.readingStarted, { bubbles: true });
  }
}

/**
 * Event class for answering a single YUSUFZ Reading question
 * @extends {Event}
 */
export class ReadingQuestionAnsweredEvent extends Event {
  /**
   * @param {number} questionIndex - Zero-based index of the answered question
   * @param {number} questionCount - Total number of questions in the quiz
   * @param {string[]} slugs - Archetype slugs the chosen answer weights toward
   */
  constructor(questionIndex, questionCount, slugs) {
    super(ThemeEvents.readingQuestionAnswered, { bubbles: true });
    this.detail = {
      questionIndex,
      questionCount,
      slugs,
    };
  }
}

/**
 * Event class for completing every question in the YUSUFZ Reading quiz
 * @extends {Event}
 */
export class ReadingCompletedEvent extends Event {
  constructor() {
    super(ThemeEvents.readingCompleted, { bubbles: true });
  }
}

/**
 * Event class for a YUSUFZ Reading result archetype being produced
 * @extends {Event}
 */
export class ReadingArchetypeProducedEvent extends Event {
  /** @param {string} slug - The winning archetype's slug */
  constructor(slug) {
    super(ThemeEvents.readingArchetypeProduced, { bubbles: true });
    this.detail = {
      slug,
    };
  }
}

/**
 * Event class for clicking a product recommended by a YUSUFZ Reading result
 * @extends {Event}
 */
export class ReadingProductClickedEvent extends Event {
  /**
   * @param {string} slug - The archetype slug the product was recommended under
   * @param {string} productUrl - The clicked product's URL
   */
  constructor(slug, productUrl) {
    super(ThemeEvents.readingProductClicked, { bubbles: true });
    this.detail = {
      slug,
      productUrl,
    };
  }
}

/**
 * Event class for a product being added to or removed from the wishlist
 * @extends {Event}
 */
export class WishlistUpdateEvent extends Event {
  /**
   * @param {string} handle - The product handle
   * @param {boolean} saved - True if the product is now saved, false if it was just removed
   */
  constructor(handle, saved) {
    super(ThemeEvents.wishlistUpdate, { bubbles: true });
    this.detail = {
      handle,
      saved,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}
